import React from 'react';
import { Text, StyleSheet, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import dayjs from 'dayjs';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import Translations from 'src/core/utils/Translations';
import { limitReached, seatsLeft } from 'src/core/utils/formChoiceLimits';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { pushPage } from 'src/presentation/helpers/NavigationHelper';
import type { ApiFormQuestion, ApiFormQuestionOption } from 'src/core/models/api/shared/ApiFormQuestion';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';

/**
 * What an option from the programme adds to a choice row: when and where it is, how many places are left, and
 * a way to read up on it (#402).
 */

/**
 * "10:00 - 11:00 · Dag 1 · Grote zaal", or nothing for a typed-in option.
 */
export function whenLabel(option: ApiFormQuestionOption, withDay = true): string {
	const parts: string[] = [];

	if (option.starts_at) {
		const start = dayjs.unix(option.starts_at).format(Constants.timeFormat);
		parts.push(option.ends_at ? `${start} - ${dayjs.unix(option.ends_at).format(Constants.timeFormat)}` : start);
	}

	if (withDay && option.day_label) parts.push(option.day_label);
	if (option.stage_title) parts.push(option.stage_title);

	return parts.join(' · ');
}

/** Seats left rather than a tally: the participant is choosing, not counting. */
export function seatsLabel(option: ApiFormQuestionOption): string | null {
	const left = seatsLeft(option);

	if (left === null) return null;

	return left === 0 ? Translations.textChoiceFull : Translations.textSeatsLeft(left);
}

interface ChoiceInfoProps {
	data: ApiFormQuestion & { show_limit?: boolean };
	option: ApiFormQuestionOption;
	/** False when the list groups by day, so the heading carries it instead. */
	withDay?: boolean;
}

/** The lines under the option name: when it is, and what is left of it. */
export function ChoiceInfo({ data, option, withDay = true }: ChoiceInfoProps) {
	const when = whenLabel(option, withDay);
	const seats = seatsLabel(option);

	return (
		<>
			{when ? <Text style={styles.infotext}>{when}</Text> : null}
			{data.show_limit ? (
				<Text style={styles.infotext}>{Translations.textParticipantsOf(option.total_submissions, option.limit ?? 0)}</Text>
			) : seats ? (
				<Text style={[styles.infotext, limitReached(option) && styles.full]}>{seats}</Text>
			) : null}
		</>
	);
}

/**
 * Whether there is anything to read behind an option.
 */
export function hasDetail(option: ApiFormQuestionOption): boolean {
	if (option.has_detail === false) return false;

	return Boolean(option.page_id_path) || option.timetable_page_id !== undefined;
}

/**
 * The chevron on the right of a choice row, or nothing when the option has no detail screen.
 */
export function ChoiceDetailLink({ option }: { option: ApiFormQuestionOption }) {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const eventStore = useEventStore();

	if (!hasDetail(option)) return null;

	const openSession = () => {
		// A timetable day hangs under its holder, so it takes the whole path.
		const path = option.timetable_page_id_path ?? String(option.timetable_page_id);
		const day = eventStore.getPageByID(path.includes('/') ? path.split('/') : [path]);

		if (!day || day.type !== 'TimetableDay') return;

		// Look the session up by its own uid: its position shifts as soon as the timetable is reordered.
		const blocks = day.data?.timetable ?? [];
		const found = blocks
			.flatMap((block) => (block.items ?? []).map((item) => ({ item, stageTitle: block.title ?? '' })))
			.find((entry) => entry.item.uid === option.timetable_session_uid);

		if (!found) return;

		navigation.navigate(Constants.screenTimeTablePage, {
			data: {
				...found.item,
				stageTitle: found.stageTitle,
				navTitle: day.name,
				eventUuid: day.event_uuid ?? '',
			},
			title: day.name,
			// Read-only from the form: the choice decides the favourite, not a heart here.
			showFavorite: false,
		});
	};

	const openDetail = () => {
		if (option.timetable_page_id !== undefined) {
			openSession();
			return;
		}

		const path = option.page_id_path;
		if (!path) return;

		const page = eventStore.getPageByID(path.includes('/') ? path.split('/') : [path]);
		if (!page) return;

		pushPage(navigation, page.type, page);
	};

	return (
		<TouchableOpacity
			accessibilityLabel={Translations.buttonViewProgramPart}
			accessibilityRole="button"
			hitSlop={{ top: 12, bottom: 12, left: 8, right: 12 }}
			style={styles.detailButton}
			onPress={openDetail}
		>
			{/* Named, not a bare chevron: an arrow says there is more, not that it is what this session is about. */}
			<Text style={styles.detailLabel}>{Translations.labelChoiceInfo}</Text>
			<MaterialIcons color={Colors.greyTextDark} name="chevron-right" size={22} />
		</TouchableOpacity>
	);
}

const styles = StyleSheet.create({
	infotext: {
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize13,
		// Not greyText (#BBBBBB): that is 1.9:1 on white, well under the 4.5:1 WCAG AA asks for.
		color: Colors.greySecondaryText,
	},
	full: {
		color: Colors.redError,
	},
	detailButton: {
		// A divider so the row reads as two targets: choosing on the left, reading up on the right.
		borderLeftWidth: 1,
		borderLeftColor: Colors.greyBorder,
		paddingLeft: Dimens.padding8,
		marginLeft: Dimens.margin8,
		alignSelf: 'stretch',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
	},
	detailLabel: {
		fontFamily: Fonts.montserratMedium,
		fontSize: Dimens.fontSize13,
		color: Colors.greyTextDark,
	},
});
